// FogMap — the per-tile byte map: low nibble fog (kbOpaque=0 .. kbEmpty=15, with the
// edge-pattern values between), high nibble galaxite. The galaxite accessors and the
// miner search are MP-hashed sim state; the fog nibble is MACHINE-LOCAL visibility
// (each client reveals around its own units) that gates FindNearestGalaxite for human
// players in SP and the IsCovered trigger condition. Drawing/minimap arms are host.
// game/fogmap.cpp + game/ht.h:1362-1378 + the reveal patterns at GameObjects.cpp:6-52.

import type { SimContext } from '../context.ts';
import type { TPoint } from '../types.ts';

export const kbOpaque = 0;
export const kbEmpty = 15;
export const kbfFogMask = 0x000f;
export const kbfGalaxiteMask = 0x00f0;
export const kcGalaxiteShift = 4;

// game/ht.h:1362-1367
export interface RevealPattern {
  ctx: number;
  cty: number;
  ab: number[];
}

// game/GameObjects.cpp:6-20 — the standard unit reveal (9x9)
export const grvlp: RevealPattern = {
  ctx: 9,
  cty: 9,
  ab: [
    0xf, 0xf, 0xa, 0x2, 0x2, 0x2, 0x3, 0xf, 0xf, 0xf, 0xa, 0x0, 0x0, 0x0, 0x0, 0x0, 0x3, 0xf, 0xa,
    0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x3, 0x8, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x1, 0x8, 0x0,
    0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x1, 0x8, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x1, 0xc, 0x0, 0x0,
    0x0, 0x0, 0x0, 0x0, 0x0, 0x5, 0xf, 0xc, 0x0, 0x0, 0x0, 0x0, 0x0, 0x5, 0xf, 0xf, 0xf, 0xc, 0x4,
    0x4, 0x4, 0x5, 0xf, 0xf,
  ],
};

// game/GameObjects.cpp:22-52 — the kfUntcLargeDefog reveal (11x11; the LIVE #else arm)
export const grvlpLarge: RevealPattern = {
  ctx: 11,
  cty: 11,
  ab: [
    0xf, 0xf, 0xf, 0xa, 0x2, 0x2, 0x2, 0x3, 0xf, 0xf, 0xf, 0xf, 0xa, 0x2, 0x0, 0x0, 0x0, 0x0, 0x0,
    0x2, 0x3, 0xf, 0xf, 0x8, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x1, 0xf, 0xa, 0x0, 0x0, 0x0, 0x0,
    0x0, 0x0, 0x0, 0x0, 0x0, 0x3, 0x8, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x1, 0x8, 0x0,
    0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x1, 0x8, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0,
    0x1, 0xc, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x0, 0x5, 0xf, 0x8, 0x0, 0x0, 0x0, 0x0, 0x0,
    0x0, 0x0, 0x1, 0xf, 0xf, 0xc, 0x4, 0x0, 0x0, 0x0, 0x0, 0x0, 0x4, 0x5, 0xf, 0xf, 0xf, 0xf, 0xc,
    0x4, 0x4, 0x4, 0x5, 0xf, 0xf, 0xf,
  ],
};

/** The C++ gpmm reaches inside FogMap (fogmap.cpp:180/265/321/577/596) — the
 *  minimap-content recompute hooks. SimContext.host satisfies this; the Level
 *  wires it at fog-map creation. Fire-and-forget, sim-neutral. */
export interface FogMapMiniMapHost {
  MiniMapRedraw(): void;
  MiniMapRedrawTile(tx: number, ty: number): void;
  MiniMapRedrawTRect(txLeft: number, tyTop: number, txRight: number, tyBottom: number): void;
}

export class FogMap {
  /** gpmm (presentation; null headless — the C++ null-checks the galaxite sites
   *  and the others only run with a live game UI). */
  host: FogMapMiniMapHost | null = null;
  private m_pbMap: Uint8Array = new Uint8Array(0);
  private m_ctxMap = 0;
  private m_ctyMap = 0;
  // game/fogmap.cpp:5-29 — pattern-blend table; 1001 and 0110 are illegal results
  private m_mpSrcDstResult = new Uint8Array(256);

  constructor() {
    for (let bSrc = 0; bSrc < 16; bSrc++) {
      for (let bDst = 0; bDst < 16; bDst++) {
        let bRes = ~(~bDst & ~bSrc) & 0xf;
        if (bRes === 9 || bRes === 6) bRes = 0;
        this.m_mpSrcDstResult[((~bSrc & 0xf) << 4) | bDst] = bRes;
      }
    }
  }

  // ht.h FogMap::Init(sizTile, sizMap) — dimensions in tiles here; all tiles start
  // fogged (kbOpaque == 0, so the zero-fill IS the C++ memset) and galaxite-free
  Init(ctxMap: number, ctyMap: number): boolean {
    this.m_ctxMap = ctxMap;
    this.m_ctyMap = ctyMap;
    this.m_pbMap = new Uint8Array(ctxMap * ctyMap);
    return true;
  }

  // game/fogmap.cpp:115-134 (knVerFogMapState 4) — fog + galaxite nibbles together
  LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 4) return false;
    pstm.ReadBytesRLE(this.m_pbMap);
    return pstm.IsSuccess();
  }

  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(4);
    pstm.WriteBytesRLE(this.m_pbMap);
    return pstm.IsSuccess();
  }

  // game/fogmap.cpp:136-157
  IsCovered(ptrc: { left: number; top: number; right: number; bottom: number }): boolean {
    const left = Math.max(ptrc.left, 0);
    const top = Math.max(ptrc.top, 0);
    const right = Math.min(ptrc.right, this.m_ctxMap);
    const bottom = Math.min(ptrc.bottom, this.m_ctyMap);
    if (left >= right || top >= bottom) return false; // no intersection

    for (let ty = top; ty < bottom; ty++) {
      for (let tx = left; tx < right; tx++) {
        if ((this.m_pbMap[ty * this.m_ctxMap + tx] & kbfFogMask) !== 0) return false;
      }
    }
    return true;
  }

  // game/fogmap.cpp:159-181 (update-map invalidation is host; fogmap.cpp:180
  // gpmm->Redraw() — the whole-minimap recompute)
  RevealAll(): void {
    for (let i = 0; i < this.m_pbMap.length; i++) {
      this.m_pbMap[i] = (this.m_pbMap[i] & ~kbfFogMask) | kbEmpty;
    }
    this.host?.MiniMapRedraw();
  }

  // game/fogmap.cpp:183-272 — rect reveal: patterned edges/corners, kbEmpty interior
  Reveal(ptrc: { left: number; top: number; right: number; bottom: number }): void {
    const W = this.m_ctxMap;
    const map = this.m_pbMap;
    const ctxT = ptrc.right - ptrc.left;

    // Top row and corners
    let i = ptrc.top * W + ptrc.left;
    map[i] |= 15 - 10;
    i++;
    for (let n = ctxT - 2; n > 0; n--, i++) map[i] |= 15 - 2;
    map[i] |= 15 - 3;

    // Bottom row and corners
    i = (ptrc.bottom - 1) * W + ptrc.left;
    map[i] |= 15 - 12;
    i++;
    for (let n = ctxT - 2; n > 0; n--, i++) map[i] |= 15 - 4;
    map[i] |= 15 - 5;

    // Left column
    i = (ptrc.top + 1) * W + ptrc.left;
    for (let n = ptrc.bottom - ptrc.top - 2; n > 0; n--, i += W) map[i] |= 15 - 8;

    // Right column
    i = (ptrc.top + 1) * W + ptrc.right - 1;
    for (let n = ptrc.bottom - ptrc.top - 2; n > 0; n--, i += W) map[i] |= 15 - 1;

    // Interior
    const left = ptrc.left + 1;
    const top = ptrc.top + 1;
    const right = ptrc.right - 1;
    const bottom = ptrc.bottom - 1;
    for (let ty = top; ty < bottom; ty++) {
      for (let tx = left; tx < right; tx++) map[ty * W + tx] |= kbEmpty;
    }
    // fogmap.cpp:265 gpmm->RedrawTRect(ptrc) (update-map invalidation is host)
    this.host?.MiniMapRedrawTRect(ptrc.left, ptrc.top, ptrc.right, ptrc.bottom);
  }

  // game/fogmap.cpp:274-329 — pattern reveal centered on (txMap, tyMap), clipped, blended
  // through the src/dst table (tile invalidations are host)
  RevealPattern(txMap: number, tyMap: number, prvlp: RevealPattern): void {
    let ctx = prvlp.ctx;
    let cty = prvlp.cty;
    let tx = txMap - (ctx >> 1);
    let ty = tyMap - (cty >> 1);

    let txPattern = 0;
    let tyPattern = 0;
    if (tx < 0) {
      ctx = tx + ctx;
      txPattern = -tx;
      tx = 0;
    }
    if (ty < 0) {
      cty = ty + cty;
      tyPattern = -ty;
      ty = 0;
    }
    if (tx + ctx > this.m_ctxMap) ctx = this.m_ctxMap - tx;
    if (ty + cty > this.m_ctyMap) cty = this.m_ctyMap - ty;

    const W = this.m_ctxMap;
    const map = this.m_pbMap;
    let iDst = ty * W + tx;
    let iSrc = tyPattern * prvlp.ctx + txPattern;
    const cbNextSrc = prvlp.ctx - ctx;
    const cbNextDst = W - ctx;
    for (let tyT = 0; tyT < cty; tyT++) {
      for (let txT = 0; txT < ctx; txT++) {
        const bDst = map[iDst];
        const bDstNew =
          (bDst & ~kbfFogMask) | this.m_mpSrcDstResult[(prvlp.ab[iSrc] << 4) | (bDst & kbfFogMask)];
        if (bDst !== bDstNew) {
          map[iDst] = bDstNew;
          // fogmap.cpp:321 gpmm->RedrawTile per CHANGED tile (pupd
          // InvalidateTile is host; per-frame full invalidation covers it)
          this.host?.MiniMapRedrawTile(tx + txT, ty + tyT);
        }
        iDst++;
        iSrc++;
      }
      iSrc += cbNextSrc;
      iDst += cbNextDst;
    }
  }

  // game/fogmap.cpp:540-546
  SetGalaxite(nGx: number, tx: number, ty: number): void {
    const i = ty * this.m_ctxMap + tx;
    this.m_pbMap[i] = (this.m_pbMap[i] & ~kbfGalaxiteMask) | ((nGx << kcGalaxiteShift) & 0xff);
  }

  // game/fogmap.cpp:548-563 — off-map lookups return none
  GetGalaxite(tx: number, ty: number): number {
    if (tx < 0 || ty < 0 || tx >= this.m_ctxMap || ty >= this.m_ctyMap) return 0;
    return (this.m_pbMap[ty * this.m_ctxMap + tx] & kbfGalaxiteMask) >> kcGalaxiteShift;
  }

  GetMapPtr(): Uint8Array {
    return this.m_pbMap;
  }

  // game/fogmap.cpp:565-578 (the pupd InvalidateTile arm is host; fogmap.cpp:577
  // gpmm->RedrawTile — gated `if (gpmm != NULL)`, the `?.` here)
  IncGalaxite(tx: number, ty: number): void {
    const i = ty * this.m_ctxMap + tx;
    this.m_pbMap[i] =
      (this.m_pbMap[i] & ~kbfGalaxiteMask) |
      ((s_abGxInc[(this.m_pbMap[i] & kbfGalaxiteMask) >> kcGalaxiteShift] << kcGalaxiteShift) &
        0xff);
    this.host?.MiniMapRedrawTile(tx, ty);
  }

  // game/fogmap.cpp:580-599 — returns whether any remains (fogmap.cpp:596
  // gpmm->RedrawTile as IncGalaxite)
  DecGalaxite(tx: number, ty: number): boolean {
    const i = ty * this.m_ctxMap + tx;
    const bNew =
      (s_abGxDec[(this.m_pbMap[i] & kbfGalaxiteMask) >> kcGalaxiteShift] << kcGalaxiteShift) & 0xff;
    this.m_pbMap[i] = (this.m_pbMap[i] & ~kbfGalaxiteMask) | bNew;
    this.host?.MiniMapRedrawTile(tx, ty);
    return bNew !== 0;
  }

  // game/fogmap.cpp:601-744 — expanding-ring search. Candidate ACCUMULATION ORDER (top
  // row L->R, bottom row, right col, left col, per radius) and the final
  // GetRandom() % count pick are hash-load-bearing. At radius 3 the ORIGIN tile itself
  // is checked first (galaxite under the miner counts as "2.5 tiles away"). The fog
  // gate is skipped for computer players and ALWAYS in multiplayer (fIgnoreFog).
  FindNearestGalaxite(
    ctx: SimContext,
    txOrigin: number,
    tyOrigin: number,
    fIgnoreFog: boolean,
  ): TPoint | null {
    const atptGx: TPoint[] = [];
    const has = (i: number) => (this.m_pbMap[i] & kbfGalaxiteMask) !== 0;
    const fogged = (i: number) => (this.m_pbMap[i] & kbfFogMask) === kbOpaque;

    const nMax1 = Math.max(txOrigin, this.m_ctxMap - txOrigin);
    const nMax2 = Math.max(tyOrigin, this.m_ctyMap - tyOrigin);
    const nRadiusMax = Math.max(nMax1, nMax2);

    for (let nRadius = 1; nRadius < nRadiusMax; nRadius++) {
      if (nRadius === 3) {
        const i = tyOrigin * this.m_ctxMap + txOrigin;
        if (has(i)) return { tx: txOrigin, ty: tyOrigin };
      }

      atptGx.length = 0;

      // across the top
      let txMin = txOrigin - nRadius;
      if (txMin < 0) txMin = 0;
      let txMax = txOrigin + nRadius;
      if (txMax > this.m_ctxMap) txMax = this.m_ctxMap;

      let ty = tyOrigin - nRadius;
      if (ty >= 0) {
        for (let tx = txMin; tx < txMax; tx++) {
          const i = ty * this.m_ctxMap + tx;
          if (!has(i)) continue;
          if (fogged(i) && !fIgnoreFog) continue;
          atptGx.push({ tx, ty });
        }
      }

      // across the bottom
      txMax += 1;
      if (txMax > this.m_ctxMap) txMax = this.m_ctxMap;
      txMin = txOrigin - nRadius + 1;
      if (txMin < 0) txMin = 0;

      ty = tyOrigin + nRadius;
      if (ty < this.m_ctyMap) {
        for (let tx = txMin; tx < txMax; tx++) {
          const i = ty * this.m_ctxMap + tx;
          if (!has(i)) continue;
          if (fogged(i) && !fIgnoreFog) continue;
          atptGx.push({ tx, ty });
        }
      }

      // down the right side
      let tyMin = tyOrigin - nRadius;
      if (tyMin < 0) tyMin = 0;
      let tyMax = tyOrigin + nRadius;
      if (tyMax > this.m_ctyMap) tyMax = this.m_ctyMap;

      let tx = txOrigin + nRadius;
      if (tx < this.m_ctxMap) {
        for (let tyT = tyMin; tyT < tyMax; tyT++) {
          const i = tyT * this.m_ctxMap + tx;
          if (!has(i)) continue;
          if (fogged(i) && !fIgnoreFog) continue;
          atptGx.push({ tx, ty: tyT });
        }
      }

      // down the left side
      tyMin = tyOrigin - nRadius + 1;
      if (tyMin < 0) tyMin = 0;
      tyMax += 1;
      if (tyMax > this.m_ctyMap) tyMax = this.m_ctyMap;

      tx = txOrigin - nRadius;
      if (tx >= 0) {
        for (let tyT = tyMin; tyT < tyMax; tyT++) {
          const i = tyT * this.m_ctxMap + tx;
          if (!has(i)) continue;
          if (fogged(i) && !fIgnoreFog) continue;
          atptGx.push({ tx, ty: tyT });
        }
      }

      if (atptGx.length !== 0) {
        const i = ctx.rng.GetRandom() % atptGx.length; // SYNC RNG
        return { ...atptGx[i] };
      }
    }

    return null;
  }
}

// game/fogmap.cpp:420-421 — galaxite inc/dec lookup tables (nibble -> nibble)
const s_abGxInc = [0, 2, 3, 4, 5, 5, 7, 8, 9, 10, 10, 12, 13, 14, 15, 15];
const s_abGxDec = [0, 0, 1, 2, 3, 4, 0, 6, 7, 8, 9, 0, 11, 12, 13, 14];
// (the previous private kbOpaque here was 0x0f — WRONG: ht.h:1369 kbOpaque is 0, fully
// fogged is the ZERO nibble. It only escaped notice because the MP fog gate is bypassed.
// The exported kbOpaque=0 at the top is the correct one and FindNearestGalaxite's
// fogged() check now uses it.)
